/**
 * InlineSparkline — SVG polyline pur, ultra-compact, sans dépendance externe.
 *
 * PHASE 3.0 UX Revolution (DECISIONS #167).
 * Réutilisable : widgets SMART sidebar + cellules DenseDataTable.
 */

interface InlineSparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  className?: string;
}

/**
 * Normalize array to [0, 1] range. Returns empty array if all same value.
 */
function normalize(data: number[]): number[] {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min;
  if (range === 0) return data.map(() => 0.5);
  return data.map((v) => (v - min) / range);
}

export function InlineSparkline({
  data,
  width = 100,
  height = 11,
  color = '#1428A0',
  className,
}: InlineSparklineProps) {
  if (!data || data.length < 2)
    return <span className="text-[9px] text-muted-foreground">—</span>;

  const normalized = normalize(data);
  const pad = 1;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  const points = normalized
    .map((v, i) => {
      const x = pad + (i / (normalized.length - 1)) * innerW;
      const y = pad + (1 - v) * innerH; // invert Y (SVG y=0 is top)
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
